import { View } from 'foundations-components/components/View';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { CommerceHubFaqs } from 'pricing-pages-ui-components/frequentlyAskedQuestions/CommerceHubFaqs';
import { PPUIPageHeader } from 'pricing-pages-ui-components/semantic/PageHeader';
import { PageSection } from 'pricing-pages-ui-components/semantic/PageSection';
import { useEffect, useState } from 'react';
import { buildFilteredHighlightedProducts } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import BannerSection from '../components/banners/BannerSection';
import { IntegrationsSection } from '../v2/containers/IntegrationsSection';
import ProductPageSubtitle from '../components/common/ProductPageSubtitle';
import ProductPageTitle from '../components/common/ProductPageTitle';
import { CreditsCallout } from '../components/common/credits-callout/CreditsCallout';
import FallbackErrorBanner from '../components/common/loading/FallbackErrorBanner';
import ProductFeatureTable from '../components/feature-table/ProductFeatureTable';
import { getI18nKeyOverrideForProductCard } from '../components/merch-card/I18nUtils';
import { ProductCard } from '../components/merch-card/cards/product-card/ProductCard';
import MobileMerchCardSelectorButtons from '../components/mobile/MobileMerchCardSelectorButtons';
import MobileCardWrapper from '../components/mobile/containers/MobileCardWrapper';
import MobileWrapper from '../components/mobile/containers/MobileWrapper';
import { COMMERCE_HUB_PAGE_CONTENT, COMMERCE_PAGE } from '../constants/ErrorBoundaryNames';
import { DEFAULT } from '../constants/ErrorCauses';
import { NavItemEnum } from '../constants/routing/NavItems';
import { useAnchorLinkScroll } from '../hooks/htmlElementHooks';
import { useGlobalLoading } from '../hooks/loadingHooks';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useRedirectIfIneligible } from '../hooks/useRedirectIfIneligible';
import { useAppSelector } from '../reducers';
import { isUngatedForMarketingPlusSelector } from '../selectors/GatingSelectors';
import { useQueryParamTerm, useQueryParamsEffect } from '../utils/urls/appQueryParamUtilsLegacy';
import PaymentsProcessorsSectionWrapper from '../v2/components/commerce-hub/PaymentsProcessorsSectionWrapper';
import { ErrorBoundary } from '../v2/components/error-boundary/ErrorBoundary';
import { ERROR_SEVERITY } from '../v2/components/error-boundary/ErrorSeverity';
import withErrorBoundary from '../v2/components/error-boundary/withErrorBoundary';
import PhoneSupportFooter from '../v2/components/phone-support/PhoneSupportFooter';
import { SCREENS } from '../v2/constants/tracking/Screens';
import { MERCH_CARD, TRACKING_SOURCES } from '../v2/constants/tracking/TrackingSources';
import { trackPageView } from '../v2/utils/tracking/trackingEventUtils';
import ErrorPage from './ErrorPage';
import MissingInfoFallbackPage from './MissingInfoFallbackPage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const commerceHubProductLevels = ['PROFESSIONAL', 'ENTERPRISE'];
const MobileCommerceHubPage = () => {
  const navItem = NavItemEnum.COMMERCE;
  const screen = SCREENS.COMMERCE;
  const navMarkerName = `${navItem.toUpperCase()}_PAGE`;
  useAnchorLinkScroll();
  useDocumentTitle(navItem);
  const isAppReady = useGlobalLoading();
  const [selectedProductLevel, setSelectedProductLevel] = useState('PROFESSIONAL');
  const isUngatedForMarketingPlus = useAppSelector(isUngatedForMarketingPlusSelector);
  useRedirectIfIneligible({
    condition: () => !isUngatedForMarketingPlus
  });
  useEffect(() => {
    if (!isAppReady) return;
    trackPageView({
      screen
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAppReady]);
  const productLevelToHighlightedProductMap = {
    ['PROFESSIONAL']: 'commerce-hub-professional',
    ['ENTERPRISE']: 'commerce-hub-enterprise'
  };
  const {
    queryParamTerm
  } = useQueryParamTerm();
  useQueryParamsEffect({
    term: queryParamTerm
  });
  if (!isAppReady) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {
      minHeight: 200,
      size: "md",
      grow: true
    });
  }
  const apiName = productLevelToHighlightedProductMap[selectedProductLevel];
  const i18nKeys = getI18nKeyOverrideForProductCard(apiName);
  const filteredHighlightedProducts = buildFilteredHighlightedProducts({
    productLevels: [selectedProductLevel],
    productLine: 'COMMERCE'
  });
  return /*#__PURE__*/_jsx(ErrorBoundary, {
    fallback: /*#__PURE__*/_jsx(MissingInfoFallbackPage, {
      navItem: navItem
    }),
    errorBoundaryName: COMMERCE_HUB_PAGE_CONTENT,
    severity: ERROR_SEVERITY.HIGH,
    metricsOptions: {
      route: navItem
    },
    children: /*#__PURE__*/_jsxs(MobileWrapper, {
      navItem: navItem,
      navMarkerName: navMarkerName,
      children: [/*#__PURE__*/_jsx(BannerSection, {
        screen: screen
      }), /*#__PURE__*/_jsx(FallbackErrorBanner, {}), /*#__PURE__*/_jsxs(PPUIPageHeader, {
        children: [/*#__PURE__*/_jsx(ProductPageTitle, {
          nonProductIdentifier: navItem
        }), /*#__PURE__*/_jsx(ProductPageSubtitle, {
          nonProductIdentifier: navItem
        })]
      }), productLevelToHighlightedProductMap[selectedProductLevel] && /*#__PURE__*/_jsxs(PageSection, {
        children: [/*#__PURE__*/_jsx(MobileMerchCardSelectorButtons, {
          options: commerceHubProductLevels,
          onClick: setSelectedProductLevel,
          selectedOption: selectedProductLevel
        }), /*#__PURE__*/_jsx(MobileCardWrapper, {
          children: /*#__PURE__*/_jsx(ProductCard, {
            apiName: apiName,
            Title: i18nKeys === null || i18nKeys === void 0 ? void 0 : i18nKeys.Title,
            Description: i18nKeys === null || i18nKeys === void 0 ? void 0 : i18nKeys.Description,
            screen: screen,
            columnStart: 1,
            trackingSource: MERCH_CARD
          })
        }), /*#__PURE__*/_jsx(View, {
          marginBlock: "800",
          children: /*#__PURE__*/_jsx(CreditsCallout, {
            navItem: screen
          })
        }), /*#__PURE__*/_jsx(ProductFeatureTable, {
          productLine: "COMMERCE",
          highlightedProducts: filteredHighlightedProducts,
          screen: screen
        }, navItem)]
      }), /*#__PURE__*/_jsx(PageSection, {
        children: /*#__PURE__*/_jsx(PaymentsProcessorsSectionWrapper, {})
      }), /*#__PURE__*/_jsx(IntegrationsSection, {}), /*#__PURE__*/_jsx(PageSection, {
        children: /*#__PURE__*/_jsx(CommerceHubFaqs, {})
      }), /*#__PURE__*/_jsx(PhoneSupportFooter, {
        phoneSourceId: TRACKING_SOURCES.COMMERCE_FOOTER,
        screen: SCREENS.COMMERCE,
        apiName: "commerce-hub-professional"
      })]
    })
  });
};
export default withErrorBoundary(MobileCommerceHubPage, {
  FallbackComponent: () => /*#__PURE__*/_jsx(ErrorPage, {
    errorCause: DEFAULT
  }),
  errorBoundaryName: COMMERCE_PAGE,
  severity: ERROR_SEVERITY.HIGH
});